/**
 * Resident tray line rules shared by ResidentDetail (post-save Assign prompt),
 * TrayLineTab and TrayLineAssignDialog. Plain functions so they can be tested
 * without rendering the pages (__tests__/trayLineRules.test.ts).
 *
 * WinForms reference:
 * - frmDietech_Resident_AddMod.vb SaveData 6195-6204 (assign on admit),
 *   ShowTrayLineAssignRoomChanged 5682-5697 (assign on room change),
 *   5429-5430 (Assign / Override security)
 * - frmDietech_Resident_TrayLineAssignNew.vb:117 (Assign Save security)
 * - ucDietech_Resident_TrayLineOverride.vb 163-185 (Assign title),
 *   261-274 ("Auto (x / y)" from the room/bed tray line)
 * - ResidentsTrayLineOverride.vb IsInUseSetTray 366-411, SetTrayandSetValuePrivate
 *   529-572 ("Override - ..." log)
 */
import type { PatientTrayLineOverrideRow } from '../../types/patient';

/* ------------------------------------------------------------------ */
/*  Settings and security                                              */
/* ------------------------------------------------------------------ */

/** Facility settings, by their JSON name on the facility settings object. */
export const TRAY_LINE_SETTING_NAMES = {
  displayOnAdmit: 'displayTrayLineAssignOnAdmit',
  displayOnRoomChange: 'displayTrayLineAssignOnRoomChange',
  useUnitMapping: 'useUnitMapping',
} as const;

function readSetting(settings: object | null | undefined, name: string): unknown {
  if (!settings) return undefined;
  const rec = settings as Record<string, unknown>;
  if (name in rec) return rec[name];
  const lower = name.toLowerCase();
  for (const k of Object.keys(rec)) {
    if (k.toLowerCase() === lower) return rec[k];
  }
  return undefined;
}

export interface TrayLineAssignSettings {
  /** Open the Assign dialog after a new resident is saved. */
  onAdmit: boolean;
  /** Open the Assign dialog after a resident's room, floor, wing or bed changes. */
  onRoomChange: boolean;
}

export function trayLineAssignSettings(settings: object | null | undefined): TrayLineAssignSettings {
  return {
    onAdmit: !!readSetting(settings, TRAY_LINE_SETTING_NAMES.displayOnAdmit),
    onRoomChange: !!readSetting(settings, TRAY_LINE_SETTING_NAMES.displayOnRoomChange),
  };
}

/**
 * Whether the facility uses unit mapping. When the setting isn't sent, a
 * fallback decides (the resident page uses "the facility has unit rows").
 */
export function facilityUsesUnitMapping(settings: object | null | undefined, fallback: boolean): boolean {
  const v = readSetting(settings, TRAY_LINE_SETTING_NAMES.useUnitMapping);
  return typeof v === 'boolean' ? v : fallback;
}

export interface TrayLineAccess {
  /** The Assign Tray Line button (level above 2, or 2 on a new resident). */
  canOpenAssign: boolean;
  /** The Override Tray Line switch and the per-day cells (level above 2). */
  canEditOverrides: boolean;
  /** The Assign dialog's Save and its move / copy / clear tools (level above 2). */
  canSaveAssignments: boolean;
}

/** Dietech_Tray_Line_Level gates, as WinForms sets them. */
export function trayLineAccess(level: number, isNewResident: boolean): TrayLineAccess {
  return {
    canOpenAssign: level > 2 || (isNewResident && level === 2),
    canEditOverrides: level > 2,
    canSaveAssignments: level > 2,
  };
}

/* ------------------------------------------------------------------ */
/*  Post-save Assign prompts                                          */
/* ------------------------------------------------------------------ */

export interface ResidentTypeFlags {
  isBulkOrder?: boolean | null;
  isBulk1stChoice?: boolean | null;
  actAsTemplate?: boolean | null;
}

export interface ResidentLocation {
  floor?: string | null;
  wing?: string | null;
  room_No?: number | string | null;
  bed_No?: string | null;
}

export interface TrayLineDefaultsLike {
  setB: number; setL: number; setD: number;
  trayB: number; trayL: number; trayD: number;
}

function isBulk(p: ResidentTypeFlags): boolean {
  return !!p.isBulkOrder || !!p.isBulk1stChoice;
}

/**
 * After a new resident is saved (SaveData 6195-6204): the facility shows the
 * Assign dialog on admit, the user's tray line level is 2 or more, the
 * resident is on a tray line (not Bulk Order, Bulk 1st Choice or Template),
 * and a meal still has no set or tray once the save (and the server's unit
 * auto-assign) is done.
 */
export function shouldPromptAssignOnAdmit(args: {
  settings: TrayLineAssignSettings;
  trayLineLevel: number;
  resident: ResidentTypeFlags;
  saved: TrayLineDefaultsLike | null | undefined;
}): boolean {
  const { settings, trayLineLevel, resident, saved } = args;
  if (!settings.onAdmit || trayLineLevel < 2) return false;
  if (isBulk(resident) || resident.actAsTemplate) return false;
  if (!saved) return true;
  return [saved.setB, saved.trayB, saved.setL, saved.trayL, saved.setD, saved.trayD].some((v) => !v);
}

function text(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/**
 * After an existing resident is saved (ShowTrayLineAssignRoomChanged
 * 5682-5697): the facility shows the Assign dialog on a room change, the
 * resident is not Bulk Order, Bulk 1st Choice or Template, and the room,
 * floor, wing or bed changed, or the resident went from Bulk Order / Bulk
 * 1st Choice to a type on the tray line. A user with no tray line access
 * (level 0) is not shown it.
 */
export function shouldPromptAssignOnRoomChange(args: {
  settings: TrayLineAssignSettings;
  trayLineLevel: number;
  before: (ResidentLocation & ResidentTypeFlags) | null | undefined;
  after: ResidentLocation & ResidentTypeFlags;
}): boolean {
  const { settings, trayLineLevel, before, after } = args;
  if (!settings.onRoomChange || trayLineLevel < 1 || !before) return false;
  if (isBulk(after) || after.actAsTemplate) return false;
  const moved = text(before.room_No) !== text(after.room_No)
    || text(before.floor) !== text(after.floor)
    || text(before.wing) !== text(after.wing)
    || text(before.bed_No) !== text(after.bed_No);
  return moved || isBulk(before);
}

/* ------------------------------------------------------------------ */
/*  "Already in use"                                                   */
/* ------------------------------------------------------------------ */

export interface TrayLineResidentDefaults {
  pKey_Patient: number;
  fullName: string;
  set_B: number; set_L: number; set_D: number;
  tray_B: number; tray_L: number; tray_D: number;
}

export interface OtherResidentOverride {
  fKey_Patient: number;
  fullName: string;
  day: number;
  mealtime: number;
  trayLineSet: number;
  trayLineTray: number;
}

export interface TrayLineConflictMaps {
  /** `meal-set-tray` -> name, from other residents' standard assignment. */
  defaults: Map<string, string>;
  /** `day-meal-set-tray` -> name, from other residents' per-day overrides. */
  overrides: Map<string, string>;
}

export function buildConflictMaps(
  patients: readonly TrayLineResidentDefaults[],
  overrides: readonly OtherResidentOverride[],
  selfId: number,
): TrayLineConflictMaps {
  const defaults = new Map<string, string>();
  for (const p of patients) {
    if (p.pKey_Patient === selfId) continue;
    const triples: Array<[number, number, number]> = [
      [0, p.set_B, p.tray_B], [1, p.set_L, p.tray_L], [2, p.set_D, p.tray_D],
    ];
    for (const [mt, s, t] of triples) {
      if (s > 0 && t > 0 && !defaults.has(`${mt}-${s}-${t}`)) defaults.set(`${mt}-${s}-${t}`, p.fullName);
    }
  }
  const byDay = new Map<string, string>();
  for (const o of overrides) {
    if (o.fKey_Patient === selfId) continue;
    if (o.trayLineSet > 0 && o.trayLineTray > 0) {
      const key = `${o.day}-${o.mealtime}-${o.trayLineSet}-${o.trayLineTray}`;
      if (!byDay.has(key)) byDay.set(key, o.fullName);
    }
  }
  return { defaults, overrides: byDay };
}

/**
 * Who else holds a set/tray. For a day cell (`day` 1..7) that is another
 * resident's standard assignment or their override for the same day and meal
 * (IsInUseSetTray); for the standard column (`day` null) the standard
 * assignments only.
 */
export function trayLineConflict(
  maps: TrayLineConflictMaps,
  day: number | null,
  mealtime: number,
  set: number,
  tray: number,
): string | undefined {
  if (set <= 0 || tray <= 0) return undefined;
  const byDefault = maps.defaults.get(`${mealtime}-${set}-${tray}`);
  if (byDefault || day === null) return byDefault;
  return maps.overrides.get(`${day}-${mealtime}-${set}-${tray}`);
}

/* ------------------------------------------------------------------ */
/*  "Auto (x / y)" from the room/bed tray line                         */
/* ------------------------------------------------------------------ */

export interface RoomBedTrayLineLike {
  floor: string | null;
  wing: string | null;
  room: number | null;
  bed: string | null;
  set_B: number | null; set_L: number | null; set_D: number | null;
  tray_B: number | null; tray_L: number | null; tray_D: number | null;
}

function same(a: unknown, b: unknown): boolean {
  return text(a).toLowerCase() === text(b).toLowerCase();
}

/**
 * The room/bed tray line (tblRoomBed_TrayLine) slot for an unassigned meal:
 * the row for the resident's floor, wing, numeric room and bed (no bed
 * matches a row without one) that has a set for the meal. Null when none.
 */
export function roomBedAutoSlot(
  rows: readonly RoomBedTrayLineLike[],
  loc: ResidentLocation,
  mealtime: number,
): { set: number; tray: number | null } | null {
  const room = text(loc.room_No);
  if (room === '' || !/^\d+$/.test(room)) return null;
  const bed = text(loc.bed_No);
  const setKey = (['set_B', 'set_L', 'set_D'] as const)[mealtime];
  const trayKey = (['tray_B', 'tray_L', 'tray_D'] as const)[mealtime];
  if (!setKey) return null;
  const row = rows.find((r) =>
    same(r.floor, loc.floor)
    && same(r.wing, loc.wing)
    && r.room !== null && String(r.room) === String(Number(room))
    && (bed === '' ? text(r.bed) === '' : same(r.bed, bed))
    && r[setKey] !== null && r[setKey] !== undefined);
  if (!row) return null;
  return { set: row[setKey] as number, tray: row[trayKey] ?? null };
}

/** The standard / per-day cell text for a 0/0 slot: "Auto (x / y)" or "N/A". */
export function unassignedCellText(slot: { set: number; tray: number | null } | null): string {
  return slot ? `Auto (${slot.set} / ${slot.tray ?? ''})` : 'N/A';
}

/* ------------------------------------------------------------------ */
/*  Override audit notes                                               */
/* ------------------------------------------------------------------ */

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * One note per override cell a save adds or changes, in the WinForms format
 * "Override - {Day} {Meal} {SetName} {set} Tray {tray}", plus one per cell
 * the save removes (back to the standard assignment). Ordered by day, then
 * meal.
 */
export function overrideLogNotes(
  before: readonly PatientTrayLineOverrideRow[],
  after: readonly PatientTrayLineOverrideRow[],
  labels: { mealName: (mealtime: number) => string; setName: string },
): string[] {
  const key = (r: { day: number; mealtime: number }) => `${r.day}-${r.mealtime}`;
  const beforeMap = new Map(before.map((r) => [key(r), r]));
  const afterMap = new Map(after.map((r) => [key(r), r]));
  const cells = new Map<string, { day: number; mealtime: number }>();
  for (const r of [...before, ...after]) cells.set(key(r), { day: r.day, mealtime: r.mealtime });
  const ordered = Array.from(cells.values()).sort((a, b) => a.day - b.day || a.mealtime - b.mealtime);
  const notes: string[] = [];
  for (const c of ordered) {
    const b = beforeMap.get(key(c));
    const a = afterMap.get(key(c));
    const where = `${DAY_NAMES[c.day] ?? c.day} ${labels.mealName(c.mealtime)}`;
    if (a && (!b || b.trayLineSet !== a.trayLineSet || b.trayLineTray !== a.trayLineTray)) {
      notes.push(`Override - ${where} ${labels.setName} ${a.trayLineSet} Tray ${a.trayLineTray}`);
    } else if (!a && b) {
      notes.push(`Override - ${where} removed`);
    }
  }
  return notes;
}

/* ------------------------------------------------------------------ */
/*  Assign dialog title                                                */
/* ------------------------------------------------------------------ */

/**
 * "Tray Line Assignment for {name} - Unit/Room/Bed: U/R/B" (unit mapping) or
 * "... - Floor/Wing/Room/Bed: F/W/R/B", with the facility's labels. Room and
 * bed are added only when set, as WinForms does.
 */
export function assignDialogTitle(args: {
  fullName: string;
  useUnitMapping: boolean;
  unit?: string | null;
  floor?: string | null;
  wing?: string | null;
  room?: string | number | null;
  bed?: string | null;
  labels: { unit: string; floor: string; wing: string; room: string; bed: string };
}): string {
  const { fullName, useUnitMapping, labels } = args;
  const room = text(args.room) ? `/${text(args.room)}` : '';
  const bed = text(args.bed) ? `/${text(args.bed)}` : '';
  if (useUnitMapping) {
    return `Tray Line Assignment for ${fullName} - ${labels.unit}/${labels.room}/${labels.bed}: ${text(args.unit) || 'Unassigned'}${room}${bed}`;
  }
  return `Tray Line Assignment for ${fullName} - ${labels.floor}/${labels.wing}/${labels.room}/${labels.bed}: ${text(args.floor)}/${text(args.wing)}${room}${bed}`;
}
